import React, { useMemo } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import { QuestionOption } from '@/types';

type AccordianQuestionProps = {
  options?: QuestionOption[];
};

type AccordionItemData = {
  key: string;
  title: string;
  description: string;
};

const fallbackTitle = (index: number) => `Item ${index + 1}`;
const fallbackDescription = 'No additional description provided.';

export const AccordianQuestion: React.FC<AccordianQuestionProps> = ({ options }) => {
  const items = useMemo<AccordionItemData[]>(() => {
    return (options ?? []).reduce<AccordionItemData[]>((acc, option, index) => {
      const title = (option.front_text ?? '').trim();
      const description = (option.back_text ?? '').trim();

      if (!title && !description) {
        return acc;
      }

      const key = option.id != null ? String(option.id) : `index-${index}`;
      acc.push({
        key,
        title: title || fallbackTitle(index),
        description: description || fallbackDescription,
      });
      return acc;
    }, []);
  }, [options]);

  if (!items.length) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No accordion items available.
      </div>
    );
  }

  return (
    <Accordion type="multiple" className="w-full space-y-6">
      {items.map((item) => (
        <AccordionItem key={item.key} value={item.key}>
          <AccordionTrigger>
            <span className="flex-1 text-left type-body-m-semibold text-foundation-base-darkbase-dark-500">
                {item.title}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="type-body-m text-foundation-base-darkbase-dark-500">
              {item.description}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export default AccordianQuestion;
